import React, { useContext, useMemo } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useLocation } from '@reach/router';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import TwoColumn, { Alignments } from '@components/shared/TwoColumn';
import { PageSlice, SliceOverlappingItemsDetails, SliceOverlappingItemsItem } from '@contracts';
import { replaceText } from '@utils/handleTextReplacement';
import useDesktop from './useDesktop';

interface Props {
  slice: PageSlice;
}

const OverlappingItems = ({ slice }: Props) => {
  const isDesktop = useDesktop(1024);
  const sliceDetails = slice.primary as SliceOverlappingItemsDetails;
  const sliceItems = slice.items as SliceOverlappingItemsItem[];
  const {
    state: { country }
  } = useContext(Context);
  const location = useLocation();

  const shouldAddItem = (item, country) => {
    if (country === CountryCode.US) {
      if (item.type === 'overlapping' && item.text.text === 'Prescription tampons') return false;
      if (item.type !== 'overlapping' && item.text.text === 'Get prescribed tampons and hormonal contraception') return false;
    }
    return true;
  };

  const [overlappingItems, descriptionItems] = useMemo(
    () =>
      (sliceItems || []).reduce(
        ([tempOverlappingItems, tempDescriptionItems], item) => {
          const newItem = { ...item, text: { text: replaceText(item?.text?.text, country, location) } };

          if (shouldAddItem(item, country)) {
            item.type === 'overlapping' ? tempOverlappingItems.push(newItem) : tempDescriptionItems.push(newItem);
          }

          return [tempOverlappingItems, tempDescriptionItems];
        },
        [[], []]
      ),
    [sliceItems]
  );

  const renderOverlappingItems = () => (
    <OverlappingItemsWrap>
      {overlappingItems.map((ovItem) => (
        <OverlappingItemWrap key={ovItem.text?.text}>
          <OverlappingItemImageWrap>
            <OverlappingItemIconWrap>
              {ovItem?.icon?.gatsbyImageData ? <GatsbyImage image={ovItem.icon.gatsbyImageData} alt={ovItem.icon.alt || ovItem.text?.text} /> : null}
            </OverlappingItemIconWrap>
            {ovItem?.image?.gatsbyImageData ? <GatsbyImage image={ovItem.image.gatsbyImageData} alt={ovItem.image.alt || ovItem.text?.text} /> : null}
          </OverlappingItemImageWrap>
          <OverlappingItemTextWrap>
            <OverlappingItemHeading>{ovItem.heading?.text}</OverlappingItemHeading>
            <OverlappingItemText>{ovItem.text?.text}</OverlappingItemText>
          </OverlappingItemTextWrap>
        </OverlappingItemWrap>
      ))}
    </OverlappingItemsWrap>
  );

  const renderDescriptionItems = () => (
    <DescriptionsWrap>
      {descriptionItems.map((desc) => (
        <Item key={desc.text?.text}>
          <ItemImageWrap>
            {desc?.image?.gatsbyImageData ? <GatsbyImage image={desc.image.gatsbyImageData} alt={desc.image.alt || desc.text?.text} /> : null}
          </ItemImageWrap>
          <ItemTextWrap>
            <ItemText>{replaceText(desc.text.text, country, location)}</ItemText>
          </ItemTextWrap>
        </Item>
      ))}
    </DescriptionsWrap>
  );

  return (
    <Container $padding={sliceDetails?.padding?.text} $background={sliceDetails?.background?.text}>
      <Inner>
        {isDesktop ? (
          <TwoColumn gap="20px" breakpoint="1024px" mobileReverse={true} alignment={Alignments.Top}>
            {renderOverlappingItems()}
            <DescriptionsColumnWrap>
              <Heading>{sliceDetails?.heading?.text}</Heading>
              {renderDescriptionItems()}
            </DescriptionsColumnWrap>
          </TwoColumn>
        ) : (
          <ColumnWrap>
            <Heading>{sliceDetails?.heading?.text}</Heading>
            {renderOverlappingItems()}
            {renderDescriptionItems()}
          </ColumnWrap>
        )}
      </Inner>
    </Container>
  );
};

const Container = styled.div<{ $padding: string; $background: string }>`
  background: ${(props) => props.$background || Colors.cream};
  padding: ${(props) => props.$padding || '120px'};
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    padding: 40px 30px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const DescriptionsColumnWrap = styled.div`
  padding: 0 40px;

  @media (max-width: 1024px) {
    padding: 0 30px;
  }
`;

const Heading = styled.div`
  font-family: 'Domaine';
  font-weight: 600;
  font-size: 30px;
  line-height: 120%;
  color: ${Colors.navyBlue};

  @media (max-width: 768px) {
    margin-top: 0;
    text-align: center;
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

const DescriptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 30px 0;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const ItemImageWrap = styled.div`
  flex: 0 0 auto;
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const ItemTextWrap = styled.div`
  flex: 1 1 auto;
`;

const ItemText = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const OverlappingItemsWrap = styled.div`
  position: relative;
  padding: 20px 70px;

  @media (max-width: 1024px) {
    width: 100%;
    padding: auto;
  }
`;

const OverlappingItemWrap = styled.div`
  position: relative;
  display: flex;
  align-content: center;
  justify-content: space-between;
  background: ${Colors.cream};
  border: 1px solid ${Colors.ecru};
  min-width: 300px;
  border-radius: 10px;
  position: relative;
  box-shadow: 0px 2px 2px rgba(0, 29, 59, 0.1);

  &:not(:first-child) {
    margin-top: -15px;
  }

  &:nth-child(even) {
    left: 40px;
  }

  @media (max-width: 1024px) {
    min-width: 216px;
    left: -40px;

    &:nth-child(even) {
      left: 0;
    }
  }
`;

const OverlappingItemIconWrap = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;

  @media (max-width: 1024px) {
    width: 14px;

    img {
      padding: 0 !important;
      height: 14px !important;
    }
  }
`;

const OverlappingItemImageWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  width: 90px;
  height: 110px;
  background: ${Colors.ecru}50;
  border-right: 1px solid ${Colors.ecru};
  justify-content: center;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;

  @media (max-width: 1024px) {
    height: 77px;

    img {
      padding: 7px;
    }
  }
`;

const OverlappingItemTextWrap = styled.div`
  padding: 0 30px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;

  @media (max-width: 1024px) {
    padding: 0 12px;
    gap: 3px;
  }
`;

const OverlappingItemText = styled.div`
  font-family: BrownStd;
  font-weight: 700;
  font-size: 14px;
  line-height: 140%;

  @media (max-width: 1024px) {
    font-size: 10px;
  }
`;

const OverlappingItemHeading = styled.div`
  font-family: BrownStd;
  font-weight: 400;
  font-size: 10px;
  line-height: 10px;
  letter-spacing: 1px;
  color: ${Colors.navyBlue};
  opacity: 0.5;

  @media (max-width: 1024px) {
    font-size: 7px;
  }
`;

const ColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

export default OverlappingItems;
